import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import {
  FileText,
  CheckCircle,
  Lock,
  Users,
  Shield
} from 'lucide-react';

const TermsOfService = () => {
  const termsHighlights = [
    {
      title: 'User Responsibilities',
      description: 'Guidelines for responsible platform usage',
      icon: Users
    },
    {
      title: 'Service Limitations',
      description: 'Clear boundaries of our service offerings',
      icon: Lock
    },
    {
      title: 'Intellectual Property',
      description: 'Protection of our and your intellectual assets',
      icon: Shield
    }
  ];

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Terms of <span className="text-primary">Service</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Understanding the agreement between you and LLM Checks
          </p>
        </section>

        {/* Terms Highlights */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-8">
            {termsHighlights.map((section, index) => (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <section.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{section.title}</h3>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Detailed Terms */}
        <section className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle>Comprehensive Terms of Service</CardTitle>
              <CardDescription>
                Detailed explanation of our service agreement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">1. Acceptance of Terms</h3>
                  <p className="text-muted-foreground">
                    By accessing or using LLM Checks, you agree to be bound by these Terms of Service.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">2. User Account</h3>
                  <p className="text-muted-foreground">
                    You are responsible for maintaining the confidentiality of your account and password.
                    You agree to accept responsibility for all activities that occur under your account.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">3. Service Usage</h3>
                  <p className="text-muted-foreground">
                    Our service is intended for legitimate AI validation and security assessment purposes.
                    Any misuse or attempts to circumvent our systems are strictly prohibited.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">4. Intellectual Property</h3>
                  <p className="text-muted-foreground">
                    All content, features, and functionality are and will remain the exclusive property
                    of LLM Checks and its licensors.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Compliance and Changes */}
        <section className="text-center mt-16 max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Ongoing Commitment</h2>
          <p className="text-muted-foreground mb-8">
            We reserve the right to modify these terms at any time.
            Continued use of our service constitutes acceptance of the updated terms.
          </p>
          <div className="flex justify-center items-center space-x-2">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <span className="text-muted-foreground">
              Last Updated: {new Date().toLocaleDateString()}
            </span>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
